import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Plus, PencilSimple, Trash, ChatCircleText, ArrowSquareOut, EnvelopeSimple,
  Buildings, CalendarBlank, Briefcase, ListChecks, Link as LinkIcon,
} from "@phosphor-icons/react";

const OUTCOMES = [
  { value: "in_afwachting",         label: "In afwachting",            tone: "zinc"   },
  { value: "gesprek_uitgenodigd",   label: "Uitgenodigd voor gesprek", tone: "blue"   },
  { value: "aangenomen",            label: "Aangenomen",               tone: "green"  },
  { value: "afgewezen",             label: "Afgewezen",                tone: "red"    },
  { value: "ingetrokken",           label: "Ingetrokken",              tone: "amber"  },
  { value: "anders",                label: "Anders",                   tone: "zinc"   },
];
const OUTCOME_BY_VALUE = Object.fromEntries(OUTCOMES.map((o) => [o.value, o]));

function today() {
  return new Date().toISOString().slice(0, 10);
}

function displayUrl(u) {
  if (!u || !u.trim()) return "";
  return u.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function formatNlDate(iso) {
  if (!iso) return "";
  // Accept "YYYY-MM-DD" or full ISO; render as "DD-MM-YYYY".
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export default function CoacheeApplicationsListPage() {
  const navigate = useNavigate();
  const [coacheeId, setCoacheeId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // null | {id?, application_date, organization, function, outcome, organization_url}
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        if (!own) {
          setLoading(false);
          return;
        }
        setCoacheeId(own.id);
        await refresh(own.id);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refresh = async (cid = coacheeId) => {
    if (!cid) return;
    const { data } = await api.get(`/coachees/${cid}/cover-letters`);
    setItems(data);
  };

  const openNew = () => {
    setEditing({
      id: null,
      application_date: today(),
      organization: "",
      function: "",
      outcome: "in_afwachting",
      organization_url: "",
    });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing({
      id: row.id,
      application_date: row.application_date || today(),
      organization: row.organization || "",
      function: row.function || "",
      outcome: row.outcome || "in_afwachting",
      organization_url: row.organization_url || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (row) => {
    const label = row.organization || displayUrl(row.organization_url) || "deze sollicitatie";
    if (!window.confirm(`"${label}" verwijderen?`)) return;
    try {
      await api.delete(`/coachees/${coacheeId}/cover-letter/${row.id}`);
      setItems((arr) => arr.filter((x) => x.id !== row.id));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!coacheeId || !editing) return;
    setSubmitting(true);
    setError("");
    const payload = {
      application_date: editing.application_date || today(),
      organization: editing.organization.trim(),
      function: editing.function.trim(),
      outcome: editing.outcome || "in_afwachting",
      organization_url: editing.organization_url.trim(),
    };
    try {
      if (editing.id) {
        await api.patch(
          `/coachees/${coacheeId}/cover-letter/${editing.id}/meta`,
          payload,
        );
      } else {
        await api.post(`/coachees/${coacheeId}/cover-letter`, payload);
      }
      setShowModal(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const totals = useMemo(() => {
    const t = { total: items.length };
    OUTCOMES.forEach((o) => { t[o.value] = 0; });
    items.forEach((i) => { t[i.outcome] = (t[i.outcome] || 0) + 1; });
    return t;
  }, [items]);

  return (
    <AppLayout title="Sollicitaties" breadcrumbs={[{ label: "Sollicitaties" }]}>
      <div className="max-w-5xl space-y-6" data-testid="applications-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <p className="text-sm text-zinc-600 leading-relaxed">
              Houd bij waar en wanneer je gesolliciteerd hebt. Klik op
              {" "}<EnvelopeSimple size={14} className="inline -mt-0.5" />{" "}
              om voor een sollicitatie de brief te schrijven met onze AI-job-coach.
            </p>
          </div>
          <button
            type="button"
            onClick={openNew}
            data-testid="apps-new-button"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
          >
            <Plus size={16} /> Nieuwe sollicitatie
          </button>
        </div>

        {/* Quick totals */}
        {items.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="apps-totals">
            <span className="px-2.5 py-1 bg-zinc-900 text-white rounded-sm uppercase tracking-wide">
              Totaal {totals.total}
            </span>
            {OUTCOMES.map((o) => (
              totals[o.value] > 0 && (
                <span
                  key={o.value}
                  className={`px-2.5 py-1 rounded-sm uppercase tracking-wide border ${toneClass(o.tone, "soft")}`}
                >
                  {o.label}: {totals[o.value]}
                </span>
              )
            ))}
          </div>
        )}

        {error && (
          <div
            className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm"
            data-testid="apps-error"
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-zinc-500">Laden...</div>
        ) : items.length === 0 ? (
          <EmptyState onNew={openNew} />
        ) : (
          <div className="neuro-card-bold bg-white overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 border-b-2 border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              <div className="col-span-2">Datum</div>
              <div className="col-span-3">Organisatie</div>
              <div className="col-span-3">Functie</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Acties</div>
            </div>
            {items.map((row) => (
              <ApplicationRow
                key={row.id}
                row={row}
                onEdit={() => openEdit(row)}
                onDelete={() => handleDelete(row)}
                onLetter={() => navigate(`/sollicitaties/${row.id}`)}
              />
            ))}
          </div>
        )}

        {showModal && editing && (
          <ApplicationModal
            value={editing}
            onChange={setEditing}
            onSubmit={handleSubmit}
            onClose={() => { if (!submitting) { setShowModal(false); setEditing(null); } }}
            submitting={submitting}
          />
        )}
      </div>
    </AppLayout>
  );
}

function toneClass(tone, variant = "soft") {
  if (variant === "soft") {
    const m = {
      zinc:  "border-zinc-200 bg-zinc-100 text-zinc-700",
      blue:  "border-[#1E88E5]/30 bg-[#1E88E5]/10 text-[#1E88E5]",
      green: "border-[#43A047]/30 bg-[#43A047]/10 text-[#2E7D32]",
      red:   "border-[#E53935]/30 bg-[#E53935]/10 text-[#C62828]",
      amber: "border-amber-300 bg-amber-50 text-amber-800",
    };
    return m[tone] || m.zinc;
  }
  return "";
}

function ApplicationRow({ row, onEdit, onDelete, onLetter }) {
  const outcome = OUTCOME_BY_VALUE[row.outcome] || OUTCOME_BY_VALUE.in_afwachting;
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-3.5 border-b border-zinc-100 hover:bg-zinc-50/60 transition-colors items-center"
      data-testid={`apps-row-${row.id}`}
    >
      <div className="md:col-span-2 text-sm font-medium text-zinc-900" data-testid="apps-row-date">
        <span className="md:hidden text-[10px] uppercase tracking-widest text-zinc-500 mr-2">Datum</span>
        {formatNlDate(row.application_date) || "—"}
      </div>
      <div className="md:col-span-3 min-w-0" data-testid="apps-row-org">
        <span className="md:hidden text-[10px] uppercase tracking-widest text-zinc-500 mr-2">Organisatie</span>
        <span className="font-medium text-zinc-900 truncate inline-block max-w-full align-bottom">
          {row.organization || "(zonder naam)"}
        </span>
        {row.organization_url && (
          <a
            href={/^https?:\/\//i.test(row.organization_url) ? row.organization_url : `https://${row.organization_url}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 ml-1.5 text-xs text-[#1E88E5] hover:underline"
            data-testid="apps-row-url"
          >
            <ArrowSquareOut size={12} /> {displayUrl(row.organization_url)}
          </a>
        )}
      </div>
      <div className="md:col-span-3 text-sm text-zinc-700 truncate" data-testid="apps-row-function">
        <span className="md:hidden text-[10px] uppercase tracking-widest text-zinc-500 mr-2">Functie</span>
        {row.function || "—"}
      </div>
      <div className="md:col-span-2" data-testid="apps-row-outcome">
        <span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-wide font-semibold rounded-sm border ${toneClass(outcome.tone, "soft")}`}>
          {outcome.label}
        </span>
      </div>
      <div className="md:col-span-2 flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={onLetter}
          title="Schrijf de brief"
          data-testid={`apps-row-letter-${row.id}`}
          className="inline-flex items-center justify-center h-9 w-9 rounded-sm border-2 border-zinc-900 bg-zinc-900 text-white hover:bg-[#1E88E5] hover:border-[#1E88E5] transition-colors"
        >
          <ChatCircleText size={16} weight="fill" />
        </button>
        <button
          type="button"
          onClick={onEdit}
          title="Bewerk sollicitatie"
          data-testid={`apps-row-edit-${row.id}`}
          className="inline-flex items-center justify-center h-9 w-9 rounded-sm border border-zinc-300 hover:border-zinc-900 transition-colors"
        >
          <PencilSimple size={15} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Verwijderen"
          data-testid={`apps-row-delete-${row.id}`}
          className="inline-flex items-center justify-center h-9 w-9 rounded-sm border border-zinc-300 hover:border-[#E53935] hover:text-[#E53935] transition-colors"
        >
          <Trash size={15} />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="neuro-card-bold p-8 bg-white text-center" data-testid="apps-empty">
      <div className="inline-flex h-14 w-14 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm mx-auto mb-4">
        <EnvelopeSimple size={26} weight="duotone" />
      </div>
      <h3 className="font-display text-xl font-bold tracking-tight">
        Nog geen sollicitaties
      </h3>
      <p className="text-sm text-zinc-600 mt-2 max-w-md mx-auto leading-relaxed">
        Voeg je eerste sollicitatie toe. Houd per organisatie bij wanneer je hebt
        gesolliciteerd, op welke functie en wat het resultaat was — en schrijf
        daarna met de AI-job-coach een sterke motivatiebrief.
      </p>
      <button
        type="button"
        onClick={onNew}
        data-testid="apps-empty-new-button"
        className="inline-flex items-center gap-2 mt-5 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
      >
        <Plus size={16} /> Nieuwe sollicitatie
      </button>
    </div>
  );
}

function ApplicationModal({ value, onChange, onSubmit, onClose, submitting }) {
  const isEdit = !!value.id;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white p-6 max-w-lg w-full rounded-sm shadow-xl space-y-4"
        data-testid="apps-modal"
      >
        <div>
          <h3 className="font-display text-xl font-bold tracking-tight">
            {isEdit ? "Sollicitatie bewerken" : "Nieuwe sollicitatie"}
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            Vul de gegevens van deze sollicitatie in. Je kunt later nog wijzigen.
          </p>
        </div>

        <Field label="Datum" icon={CalendarBlank} testid="apps-modal-date">
          <input
            type="date"
            required
            value={value.application_date}
            onChange={(e) => onChange({ ...value, application_date: e.target.value })}
            data-testid="apps-modal-date-input"
            className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900"
          />
        </Field>

        <Field label="Organisatie" icon={Buildings} testid="apps-modal-org">
          <input
            type="text"
            required
            value={value.organization}
            onChange={(e) => onChange({ ...value, organization: e.target.value })}
            placeholder="Naam van het bedrijf of de organisatie"
            data-testid="apps-modal-org-input"
            className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900"
          />
        </Field>

        <Field label="Website (URL)" icon={LinkIcon} testid="apps-modal-url">
          <input
            type="text"
            value={value.organization_url}
            onChange={(e) => onChange({ ...value, organization_url: e.target.value })}
            placeholder="https://voorbeeld.nl"
            data-testid="apps-modal-url-input"
            className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900"
          />
        </Field>

        <Field label="Functie" icon={Briefcase} testid="apps-modal-function">
          <input
            type="text"
            value={value.function}
            onChange={(e) => onChange({ ...value, function: e.target.value })}
            placeholder="Bijv. Junior Salesmedewerker"
            data-testid="apps-modal-function-input"
            className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900"
          />
        </Field>

        <Field label="Status / Uitkomst" icon={ListChecks} testid="apps-modal-outcome">
          <select
            value={value.outcome}
            onChange={(e) => onChange({ ...value, outcome: e.target.value })}
            data-testid="apps-modal-outcome-input"
            className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900 bg-white"
          >
            {OUTCOMES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>

        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="apps-modal-cancel"
            className="px-4 py-2 text-sm font-medium border border-zinc-300 rounded-sm hover:bg-zinc-100 transition-colors"
          >
            Annuleren
          </button>
          <button
            type="submit"
            disabled={submitting || !value.organization.trim()}
            data-testid="apps-modal-submit"
            className="px-5 py-2 text-sm font-semibold tracking-wide uppercase bg-zinc-900 text-white rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
          >
            {submitting ? "Opslaan..." : isEdit ? "Wijzigingen opslaan" : "Sollicitatie toevoegen"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, icon: Icon, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
        {Icon && <Icon size={12} weight="duotone" />} {label}
      </label>
      {children}
    </div>
  );
}
